import { Button } from '../../../../components/Button';
import { MaterialIcon } from '@/components/MaterialIcon';
import Input from '../../../../components/Input';
import type { ChangeEvent } from 'react';
import { useState } from 'react';
import usePostOrganizationStaffInvite from '../api/usePostOrganizationStaffInvite';
import Select from '@/components/Select';
import { showSuccessToast, showErrorToast } from '../../job_change_processes/lib/toast';

type UserType = 'admin' | 'normal';

const USER_TYPE_OPTIONS = [
  {
    value: 'admin',
    label: '管理者ユーザー',
    comment: 'ユーザーの招待/削除など、全てのアクセスが可能です。',
  },
  {
    value: 'normal',
    label: '通常ユーザー',
    comment:
      'ユーザーの招待/削除、データのエクスポート、APIキーの出力ができません。キャリアアドバイザーのメンバーに最適です。',
  },
];

interface Props {
  onClose?: () => void;
}

export default function NewStaffPage({ onClose }: Props) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [userType, setUserType] = useState<UserType>('admin');
  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
  }>({});
  const { mutateAsync: inviteStaff } = usePostOrganizationStaffInvite();

  // バリデーション関数
  const validateForm = () => {
    const newErrors: typeof errors = {};

    // 名前のバリデーション
    if (!name.trim()) {
      newErrors.name = '氏名を入力してください';
    } else if (name.trim().length < 2) {
      newErrors.name = '氏名は2文字以上で入力してください';
    } else if (name.trim().length > 15) {
      newErrors.name = '氏名は15文字以下で入力してください';
    }

    // メールアドレスのバリデーション
    if (!email.trim()) {
      newErrors.email = 'メールアドレスを入力してください';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        newErrors.email = '正しいメールアドレスの形式で入力してください';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    // エラーをクリア
    if (errors.name) {
      setErrors((prev) => ({ ...prev, name: undefined }));
    }
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    // エラーをクリア
    if (errors.email) {
      setErrors((prev) => ({ ...prev, email: undefined }));
    }
  };

  const handleSubmit = async () => {
    // バリデーションチェック
    if (!validateForm()) {
      return;
    }

    try {
      await inviteStaff({
        name: name.trim(),
        email: email.trim(),
        is_admin: userType === 'admin' ? true : false,
      });
      showSuccessToast('招待メールを送信しました。');
      onClose?.();
    } catch (error) {
      showErrorToast('招待メールの送信に失敗しました。');
    }
  };

  return (
    <>
      <div>
        <button
          type="button"
          className="mt-5 mb-8 flex items-center pl-6 text-sm font-bold text-primary hover:text-primary_darken"
          onClick={onClose}
        >
          <MaterialIcon name="arrow_back_ios" className="mr-1 text-base" />
          メンバー管理に戻る
        </button>
        <div className="rounded-lg bg-white px-12 py-11">
          <h1 className="mb-6 text-base font-bold">新規アカウントの招待</h1>
          <div className="space-y-8 px-4">
            <div>
              <label htmlFor="name" className="block text-body-sm font-medium">
                氏名
              </label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={handleNameChange}
                placeholder="氏名"
                required
                className={`mt-2 w-full max-w-[400px] ${errors.name ? 'border-red-500' : ''}`}
              />
              {errors.name ? (
                <p className="mt-1 text-xs text-red-600">{errors.name}</p>
              ) : (
                <p className="mt-1 text-xs text-sub">2文字以上で入力してください</p>
              )}
            </div>

            <div>
              <label htmlFor="email" className="block text-body-sm font-medium">
                メールアドレス
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={handleEmailChange}
                placeholder=""
                required
                className={`mt-2 w-full max-w-[400px] ${errors.email ? 'border-red-500' : ''}`}
              />
              {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
            </div>

            <div>
              <label htmlFor="userType" className="block text-body-sm font-medium">
                アクセス権限
              </label>
              <div className="w-[236px]">
                <Select
                  variant="compact"
                  value={userType}
                  onValueChange={(value) => setUserType(value as UserType)}
                  items={USER_TYPE_OPTIONS}
                />
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <Button color="primary" size="md" onClick={handleSubmit} disabled={!name || !email}>
              招待する
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
